/**
 * ============================================================================
 * BATTLE OF THE SEXES - PREMIUM ANIMATION ORCHESTRATOR
 * Lightweight, performant, and elegant
 * Uses native Intersection Observer for zero-dependency animations
 * ============================================================================
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  const CONFIG = {
    // Intersection Observer thresholds
    threshold: 0.15, // Trigger when 15% visible
    rootMargin: '0px 0px -80px 0px', // Slight delay for premium feel
    
    // Animation classes
    classes: {
      fadeUp: 'animate-fade-up',
      fade: 'animate-fade',
      staggerParent: 'animate-stagger-parent',
      staggerItem: 'animate-stagger-item',
      sectionHeading: 'section-heading-animate',
      visible: 'is-visible',
      complete: 'animation-complete'
    },
    
    // Performance settings
    unobserveAfterReveal: true, // Unobserve after animation for performance
    removeWillChange: true, // Remove will-change after animation
    willChangeDelay: 1200 // Time to wait before removing will-change (ms)
  };

  // ===== DEVICE DETECTION =====
  const isLowEndDevice = () => {
    // Detect low-end devices to potentially skip animations
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      return true;
    }
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) {
      return true;
    }
    return false;
  };

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ===== ANIMATION ORCHESTRATOR =====
  class PremiumAnimationOrchestrator {
    constructor() {
      this.observer = null;
      this.observedElements = new Set();
      this.initialized = false;
    }

    /**
     * Initialize the animation system
     */
    init() {
      if (this.initialized) return;
      
      // Add loading class to prevent FOUC
      document.documentElement.classList.add('js-loading');
      
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    /**
     * Setup the Intersection Observer and observe elements
     */
    setup() {
      // Create Intersection Observer
      this.createObserver();
      
      // Observe all animated elements
      this.observeElements();
      
      // Mark as initialized
      this.initialized = true;
      
      // Remove loading class, add loaded class
      document.documentElement.classList.remove('js-loading');
      document.documentElement.classList.add('js-loaded');
      
      console.log('✨ Premium animations initialized');
    }

    /**
     * Create the Intersection Observer
     */
    createObserver() {
      const options = {
        threshold: CONFIG.threshold,
        rootMargin: CONFIG.rootMargin
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.revealElement(entry.target);
            
            // Unobserve after reveal for performance
            if (CONFIG.unobserveAfterReveal) {
              this.observer.unobserve(entry.target);
              this.observedElements.delete(entry.target);
            }
          }
        });
      }, options);
    }

    /**
     * Observe all elements with animation classes
     */
    observeElements() {
      // Selector for all animated elements
      const selectors = [
        `.${CONFIG.classes.fadeUp}`,
        `.${CONFIG.classes.fade}`,
        `.${CONFIG.classes.staggerParent}`,
        `.${CONFIG.classes.sectionHeading}`
      ].join(', ');

      const elements = document.querySelectorAll(selectors);
      
      elements.forEach(element => {
        // Skip if already visible (for elements in viewport on load)
        if (element.classList.contains(CONFIG.classes.visible)) {
          return;
        }
        
        // Observe the element
        this.observer.observe(element);
        this.observedElements.add(element);
      });

      console.log(`👀 Observing ${this.observedElements.size} elements for animations`);
    }

    /**
     * Reveal an element with animation
     */
    revealElement(element) {
      // Add visible class to trigger CSS animation
      element.classList.add(CONFIG.classes.visible);
      
      // If this is a stagger parent, we don't need to do anything else
      // The CSS handles the children with stagger delays
      
      // Remove will-change after animation completes (performance optimization)
      if (CONFIG.removeWillChange) {
        setTimeout(() => {
          element.classList.add(CONFIG.classes.complete);
        }, CONFIG.willChangeDelay);
      }
    }

    /**
     * Destroy the observer and clean up
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.observedElements.clear();
      this.initialized = false;
    }

    /**
     * Refresh observations (useful after dynamic content loads)
     */
    refresh() {
      this.observeElements();
    }
  }

  // ===== INITIALIZE =====
  
  // Create global instance
  window.BattleSexesAnimations = new PremiumAnimationOrchestrator();
  
  // Auto-initialize
  window.BattleSexesAnimations.init();

  // ===== EXPORT FOR MANUAL CONTROL =====
  
  // Allow manual refresh if content is dynamically loaded
  window.refreshBattleSexesAnimations = function() {
    if (window.BattleSexesAnimations) {
      window.BattleSexesAnimations.refresh();
    }
  };

  // ===== PERFORMANCE MONITORING (Development only) =====
  
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Log performance metrics in development
    window.addEventListener('load', () => {
      if (window.performance && window.performance.timing) {
        const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
        console.log(`⚡ Page loaded in ${loadTime}ms with premium animations`);
      }
    });
  }

  // ===== CLEANUP ON PAGE UNLOAD =====
  
  window.addEventListener('beforeunload', () => {
    if (window.BattleSexesAnimations) {
      window.BattleSexesAnimations.destroy();
    }
  });

})();

/**
 * ============================================================================
 * END OF PREMIUM ANIMATION ORCHESTRATOR
 * Performance-first, accessibility-aware, silky smooth ✨
 * ============================================================================
 */

